import { ModalShell } from '../design/ModalShell';
import { WizardJourney } from '../design/WizardJourney';
import { IdentityCard } from '../design/IdentityCard';
import { PreviewCard } from '../design/PreviewCard';
import { FormField } from '../design/FormField';
import { Button } from '../design/Button';
import { identityFieldsFrom, integrationsFrom } from './vmAdapters';
import type { UploadOverlayVm } from '../useUploadOverlay';

/**
 * Tool Upload P3 V2 — "Detail your instructions" (wizard step 2)
 *
 * The second numbered wizard step. Its CHROME, LEFT PANEL and COLUMN RULE are
 * identical to P2 (audit §2.8); only the right column differs — where P2 has a
 * mixed form (Tool Name / Category / Description / Tags / Time Saved), P3 has
 * four uniform action-instruction fields.
 *
 * SHARED, NOT DUPLICATED. `IdentityCard`, `PreviewCard`, `FormField`,
 * `StepPills` and `ModalShell` are all built and measured against P2 and reused
 * here verbatim. The identity/preview data comes from the same fixtures the
 * profile pages use. This page composes them; it invents no geometry of its own.
 *
 * MEASURED from `Tool Upload P3 V2.svg`, offsets relative to the modal's true
 * top-left (190, 231):
 *
 *   step pills row       y=264   (0.1 / 0.6 / 0.1 / 0.1 — step 2 active)
 *   header rule          191→1245 @ 328.5   #022B30        (see RULES below)
 *   column rule (vert)   485.5, 329→870     #022B30
 *   footer rule (horiz)  486→1245 @ 788.5   #022B30        (right column only)
 *
 *   LEFT PANEL, x=224 (34 in from the modal), width 230:
 *     "Your Identity"    heading 13px  baseline 384.727
 *     IdentityCard       224,406  230x161
 *     "Tool Preview"     heading 13px  baseline 615.727
 *     PreviewCard        224,637  230x188
 *
 *   RIGHT COLUMN, fields at x=518.5 (33 in from the rule), width 695:
 *     Action 1 …Outlook*  label 518,373.5   field 518.5,384.5  695x63
 *     Action 2 …Gmail*    label 518,476.5   field 518.5,487.5  695x63
 *     Action 3 …Click*    label 518,579.5   field 518.5,590.5  695x63
 *     Action 4 …Save*     label 518,682.5   field 518.5,693.5  695x63
 *       -> uniform: field 63, gap 40 (baseline), pitch 103. Verified with
 *          `derive gaps`: sizes UNIFORM 63, gaps UNIFORM 40.
 *     help line          518,833.5  "Are the details in your identity wrong?
 *                        Get help here." — "here" is --accent (teal, full
 *                        strength), the rest is 11px/300 black.
 *
 *   FOOTER, right-aligned (P3 has NO "Continue Draft" — that is P1 only):
 *     Cancel  1094.5,815.5  59x22  r7.5  outline #022B30 @ 0.3
 *     Next    1168.5,815.5  48x22  r7.5  outline #022B30 @ 0.3
 *       -> gap 15 (--modal-footer), right inset 29 (--modal-footer-r): the same
 *          measured values as P1's footer, so the same tokens.
 *
 * THE THREE RULES ARE PAGE-OWNED, not drawn by ModalShell. ModalShell's comment
 * currently claims P2–P5 draw no internal rules; they draw all three, on every
 * wizard frame (see SOLVE_LATER B9 for the cross-frame table). No ModalShell
 * change is needed to render P3: the header rule rides the `header` slot beneath
 * the pills, the column rule is the body grid's 1px middle track, and the footer
 * rule is the right column's own bottom group (see below) — NOT the `footer`
 * slot, which this page no longer uses. All three stay in flow.
 *
 * WHY THE BODY IS ONE GRID, RULE INCLUDED. The column rule sits between the two
 * columns and runs the full body height, so `grid-cols-wizard-body`
 * (--wizard-rule-x | 1px | minmax(0,1fr)) makes the 1px track BE the rule — a
 * bordered middle cell — rather than an absolutely-placed line. Layout stays in
 * flow; no coordinate is pinned. The header and footer rules are horizontal and
 * belong to their own bands.
 *
 * VERTICAL RHYTHM inside each column comes entirely from the measured tokens:
 * left panel uses --wizard-heading-gap / --wizard-group-gap (both derived by the
 * P2 session, see tokens.css), right column uses FormField's own
 * --label-field-gap (6) with --field-row-gap (14) between fields, applied via
 * the `gap-row-gap` utility (NOT `gap-field-row`, which is unregistered and
 * emits gap 0 — the bug this page shipped with; see SOLVE_LATER). Both gap sets
 * are line-box conversions of the raw baseline figures, not the baselines.
 *
 * THE FOOTER LIVES INSIDE THE RIGHT COLUMN, not ModalShell's footer slot — the
 * corrected P2 pattern (UploadDescribePage.tsx). P3's footer rule is drawn
 * RIGHT-COLUMN-ONLY (x=486→1245), and the left panel runs uninterrupted to the
 * modal floor; a full-width ModalShell footer band shortened the shared interior
 * and clipped the stationary left panel ~40px (measured body overflow +40 at
 * 1440). So the two-column region fills the modal height and the footer is the
 * right column's own bottom group, pushed down with mt-auto (flow, not a
 * coordinate). Both panels are STATIONARY — neither scrolls.
 *
 * WHAT CHANGED IN THE BACKEND COPY. Layout, classNames and the derivation above are
 * untouched. Only data and handlers:
 *
 *   1. `profile` / `uploadedTools` fixture reads -> the live Vm (see vmAdapters).
 *   2. THE FOUR INTEGRATION NAMES WERE PLACEHOLDERS and the frame's own note says so
 *      ("the real list comes from the integrations API"). They are now the flow's
 *      real connectors, which are already in memory — no new call. Same for the four
 *      action fields: `toolActions` was a four-row fixture, and the real list is
 *      `vm.components`, so the field count follows the automation.
 *   3. `navigate(…)` -> `vm.goToStage('details')` for Back and
 *      `vm.onNextFromInstructions` for Next — a GATE, not a navigation: it refuses
 *      to advance while any component is unexplained and focuses the first one that
 *      is (the backend's own behaviour, unchanged).
 *   4. The field label was `action.label`, a single string. A component's real
 *      identity is its hierarchical numbering plus its name plus, inside a control,
 *      which branch it sits in — "3.1 — If Yes — Send an email" — which is the whole
 *      point of the component manifest. So the label is composed from those real
 *      fields rather than flattened to one.
 *
 * The design draws exactly four action fields because the example automation has
 * four steps. A real flow has as many as it has; the uniform 63px field and the 14px
 * rhythm reproduce any count for free, which is why the map is unchanged apart from
 * its source.
 *
 * STILL TODO(dev), unchanged from the Design: the "here" link has no target.
 */
export function UploadDetailPage({ vm }: { vm: UploadOverlayVm }) {
  const { details, components, explanations, missingIds, triedStage } = vm;

  // Errors show only once the step has been submitted — `triedStage` is the backend's
  // "you tried to advance" signal. The Design draws no error state (its own note,
  // audit Q7/Q8), so this is expressed through FormField's `invalid` prop.
  const showErrors = triedStage.instructions;

  return (
    <ModalShell
      label="Detail your instructions"
      headerInset="pills"
      // The Design's four fields always fit; a real automation's may not, so the
      // FIELD GROUP scrolls (see the scroller below) rather than the body — which
      // would carry the stationary left rail and the footer with it. That needs the
      // body to be a non-scrolling flex column so `min-h-0 flex-1` has a definite
      // height to resolve against. Same reason as P2.
      bodyScroll={false}
      header={
        // The step pills, then the full-width header rule beneath them. The
        // header region is inset by --step-row-inset (65) — the pill row's own
        // wider inset, see ModalShell.headerInset — so the rule is -mx'd back
        // out by that same amount to reach the modal's edges and span 191→1245
        // as the source draws it, while the pills stay within the inset.
        <>
          <WizardJourney activeStep={2} />
          <div className="-mx-step-row mt-11 h-px bg-ink" />
        </>
      }
    >
      {/* The body: stationary left panel | 1px column rule | stationary right
          column. The middle grid track is the rule itself (a bordered cell),
          so the left/right split is in flow — no pinned line.

          h-full, and NO pt- on the grid — the corrected P2 pattern (see
          UploadDescribePage.tsx and SOLVE_LATER B10/A3). The footer is NOT a
          ModalShell band: P3's footer rule is drawn RIGHT-COLUMN-ONLY
          (x=486→1245), and a full-width ModalShell footer band stole 82.5px
          from the shared interior and clipped the stationary left panel ~40px
          short (measured: body scrollHeight 488 vs clientHeight 448 at 1440).
          So the whole two-column region fills the modal height and the footer
          is the right column's own bottom group, pushed down by mt-auto.

          The two columns start at different heights (form's first label box is
          30 below the header rule, the panel's first heading is 41.5), so each
          column carries its own top gap and the rule track spans both — a
          shared pt- would be 11.5px wrong on one of them.

          `grid-rows-[minmax(0,1fr)] min-h-0` for the same reason as P2's grid —
          see the long note there. `h-full` sizes this grid's BOX to the modal
          interior but leaves its implicit row at `auto` (fit-content), so a
          component list longer than the box overflows the modal floor instead of
          scrolling: the mt-auto footer leaves the box and the left rail draws past
          the bottom edge. Naming the row makes it fill rather than fit. P3 needs
          this at least as much as P2 — its row count is the automation's real
          component count, so it is unbounded by nature. */}
      <div className="grid h-full min-h-0 grid-rows-[minmax(0,1fr)] grid-cols-wizard-body">
        {/* LEFT PANEL. One flex column with the measured two-value rhythm:
            --wizard-heading-gap sits a heading above its card, --wizard-group
            sits a card above the next heading. Its own pt-wizard-rule-gap-panel
            (41.5) is the panel's rule-to-first-heading gap — larger than the
            form's 30, which is why the grid carries no shared pt-. The left/gap
            insets match P2 exactly (the panel is identical across P2/P3). */}
        <div className="flex min-w-0 flex-col pl-wizard-left-rest pr-wizard-left-gap pt-wizard-rule-gap-panel">
          <h2 className="text-sm font-light text-ink">Your Identity</h2>
          <IdentityCard className="mt-wizard-heading" fields={identityFieldsFrom(vm.props)} />
          <h2 className="mt-wizard-group text-sm font-light text-ink">Tool Preview</h2>
          <PreviewCard
            className="mt-wizard-heading"
            name={details.name || vm.props.flow.name}
            owner={vm.props.builderName}
            // The frame drew four placeholder names and said the real list comes
            // from the API; these are the flow's real connectors, already in
            // memory. See note 2 and integrationsFrom.
            integrations={integrationsFrom(components)}
            // See P2: the plate is a generated diagram of `components`, not a logo row.
            shape={components}
          />
        </div>

        {/* The column rule: the grid's 1px middle track, drawn as a full-height
            border rather than a placed line. */}
        <div className="bg-ink" aria-hidden="true" />

        {/* RIGHT COLUMN. A full-height flex column: the four uniform
            action-instruction fields at the top, then the footer group pushed
            to the modal floor with mt-auto (the corrected P2 pattern). The
            column's pl-wizard-right (33) sits the fields off the rule at x=518;
            its own pt-wizard-rule-gap-form (30) is the rule-to-first-label gap.
            The fields fill the remaining width (695 at 1440). */}
        <div className="flex min-w-0 flex-col pl-wizard-right">
          {/* gap-row-gap, NOT gap-field-row. gap-field-row is not a registered
              utility (it emits nothing → gap 0), so the fields sat 14px too
              tight. gap-row-gap maps --field-row-gap (14): field bottom → next
              label box top, the design's uniform transition. See SOLVE_LATER. */}
          {/* THE SCROLLER. The Design's four fields always fit the 640 box; a real
              automation can have twenty, and the alternative to scrolling here is
              scrolling the body — which carries the stationary left rail and the
              footer with it. `min-h-0 flex-1` makes the overflow resolve inside the
              flex column (see ModalShell's `bodyScroll` note).

              NO horizontal margin or padding of its own — the fields keep
              ModalShell's px-modal-x, which makes the right gutter (32.5) match the
              left one (33, pl-wizard-right), as measured on the Design's own frame.
              Only the footer rule escapes to the modal edge. See P2's fuller note,
              which records the two wrong turns here: pr-modal-x doubled the inset,
              and -mr-modal-x removed the right gutter altogether.

              THE 30 IS THE SCROLLER'S PADDING, NOT THE PARENT'S GAP — the white-bar
              fix, applied identically on P2 (see UploadDescribePage.tsx for the full
              note). Briefly: --wizard-rule-gap-form (30) used to sit as `pt-` on the
              column ABOVE this scroller, which is right in the Design because the
              Design's fields do not scroll — the 30 is simply visible gap at rest.
              Once the column scrolls, an outside gap becomes a dead strip: content
              clips 30 below the rule and that 30 is permanently blank surface, the
              "white bar above the text". Inside, as the scroller's own padding, it
              draws the same at rest and lets the text travel up through it and clip at
              the rule itself.

              The bottom is the same 30, measured not mirrored: `pb-row-gap` (14) was
              the field-to-field rhythm borrowed as an edge inset, while the frame's own
              rule-to-content gap below is --wizard-footer-help-gap (30). P3's footer
              geometry is byte-identical to P2's, so the same 30/30 holds here. */}
          {/* THE SCROLLBAR SITS AT THE MODAL WALL, not in the gutter. `-mr-modal-x`
              takes the scroller's right EDGE out through ModalShell's px-modal-x to
              the modal's inner wall, so the scrollbar is drawn there rather than
              floating 31.5px inboard of it. `pr-modal-x` then gives that same 31.5
              back as CONTENT padding, so the fields do not move — only the track
              does. Both are needed: the earlier attempt used `-mr-modal-x` alone and
              the gutter vanished with the form flush against the wall (recorded in
              the note above). Edge out, content back in. */}
          <div className="-mr-modal-x flex min-h-0 min-w-0 flex-1 flex-col gap-row-gap overflow-y-auto pb-wizard-rule-gap-form pr-modal-x pt-wizard-rule-gap-form">
            {components.map((component) => (
              <FormField
                key={component.id}
                // The component's real identity, not a flattened string: the
                // hierarchical number, the author's own step name, and the branch
                // it sits in where there is one ("3.1 — If Yes — Send an email").
                // See note 4 and FlowComponent's own field docs.
                label={`${component.numbering} — ${
                  component.branch ? `${component.branch} — ` : ''
                }${component.label}*`}
                name={`explain-${component.id}`}
                size="action"
                required
                value={explanations[component.id] ?? ''}
                onChange={(v) => vm.onExplanationChange(component.id, v)}
                invalid={showErrors && missingIds.includes(component.id)}
                registerRef={(el) =>
                  vm.registerComponentField(component.id, el as HTMLTextAreaElement | null)
                }
              />
            ))}
          </div>

          {/* FOOTER GROUP — pinned to the modal floor by flow, not coordinate,
              exactly as the corrected P2 (UploadDescribePage.tsx). mt-auto
              consumes the slack between the last field and the bottom. The band
              is the right-column-only rule (x=486→1245), then help + buttons,
              then --wizard-footer-pad-b. P3's footer geometry is byte-identical
              to P2's: rule 486→1245 @788.5, Cancel 1094.5/Next 1168.5 (59/48
              ×22 r7.5), help x=518 baseline 833.5, "here" #009D9A at 761.912. */}
          <div className="mt-auto pb-wizard-footer-b">
            {/* -ml-wizard-right reaches back from the form edge (518) to the
                column/rule boundary (486); -mr-modal-x escapes ModalShell's
                body padding to the modal's inner edge. */}
            <div className="-ml-wizard-right -mr-modal-x h-px bg-ink" />
            {/* NOT items-center: help box centre 829.5 vs button centre 826.5,
                3px apart on every upload frame. items-start + two measured top
                offsets. -mr-modal-x on the justify-between ROW pushes only the
                buttons to the modal's true right inset; pr-modal-footer-r is
                the 29 from that edge. Identical to P2's footer row. */}
            <div className="-mr-modal-x flex flex-wrap items-start justify-between gap-x-modal-footer gap-y-5 pr-modal-footer-r">
              <p className="min-w-0 pt-wizard-footer-help text-xs font-light text-ink">
                Are the details in your identity wrong? Get help{' '}
                {/* TODO(dev): opens identity/profile help. The design draws it as a link but
                    specifies no target — so it is a button styled as a link (it will trigger an
                    action, not navigate), which is also the accessible form (a bare href="#" is
                    not a valid destination). Wire the handler when the help target exists. */}
                <button
                  type="button"
                  className="text-accent-link underline-offset-2 hover:underline"
                >
                  here
                </button>
                .
              </p>
              <div className="flex shrink-0 items-start gap-modal-footer pt-wizard-footer-btn">
                {/* Returns to the previous wizard step (P2, Describe). The Design
                    navigated to '/upload/2-describe'; the backend keeps its step in
                    state, so this is the same move expressed as a stage change. */}
                <Button variant="outline-square" size="xs" onClick={() => vm.goToStage('details')}>
                  Back
                </Button>
                {/* Advances to wizard step 3. A GATE, not a navigation: it refuses
                    while any component is unexplained and focuses the first one
                    that is (note 3). */}
                <Button variant="outline-square" size="xs" onClick={vm.onNextFromInstructions}>
                  Next
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ModalShell>
  );
}
